import { useEffect, useState } from 'react';
import { useDisclosure, useToast } from '@chakra-ui/react';
import { useAuth } from '../../../context/AuthContext';
import { productRetailTransactionApi, productServicesApi } from '../../../config/api';
import { getMemberLabels } from '../config/memberIndicatorConfig';

const ORG_CODE_TO_MEMBER = {
  snh: 'RETAIL',
  ias: 'AVIATION_SERVICES',
  api: 'AIRPORTS',
  twc: 'DESTINATION',
  hin: 'HOSPITALITY',
  itdc: 'TOURISM_DEVELOPMENT',
};

const getBrandColors = (memberType) => {
  if (memberType === 'RETAIL') {
    return ['#1E88E5', '#FF6B35', '#0066CC', '#FF9800', '#42A5F5', '#FFB74D', '#0D47A1', '#E65100', '#64B5F6', '#FFCC80'];
  }
  if (memberType === 'AVIATION_SERVICES') {
    return ['#0D47A1', '#1565C0', '#546E7A', '#78909C', '#1976D2', '#607D8B', '#1E88E5', '#90A4AE', '#0277BD', '#455A64'];
  }
  return ['#1E88E5', '#FF6B35', '#4A90E2', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#06B6D4', '#84CC16'];
};

const getTodayDate = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const toApiDateRange = (from, to) => ({
  dateFrom: from.includes(' ') ? from : `${from} 00:00:00`,
  dateTo: to.includes(' ') ? to : `${to} 23:59:59`,
});

export const useIeemDashboardData = () => {
  const { user } = useAuth();
  const toast = useToast();
  const orgCode = user?.organization_code || '';
  const isInj = orgCode?.toLowerCase() === 'inj';
  const userMember = ORG_CODE_TO_MEMBER[orgCode?.toLowerCase()] || null;

  const [loading, setLoading] = useState(true);
  const [chartLoading, setChartLoading] = useState(false);
  const [dateFrom, setDateFrom] = useState(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 1);
    return d.toISOString().split('T')[0];
  });
  const [dateTo, setDateTo] = useState(() => getTodayDate());
  const [selectedMember, setSelectedMember] = useState(() => (!isInj && userMember ? userMember : null));
  const [showDetail, setShowDetail] = useState(() => !isInj && userMember !== null);

  const [dashboardData, setDashboardData] = useState({
    allInjourney: {
      totalTrx: 0,
      totalGrossAmount: 0,
      totalUser: 0,
      totalDiscAmount: 0,
      totalActiveEmployee: 11576,
      totalNetAmount: 0,
      usageRatio: 0,
      avgDiscRate: 0,
      avgTicketSize: 0,
    },
    byMember: {
      DESTINATION: { numTrx: 0, grossAmount: 0, netAmount: 0 },
      RETAIL: { numTrx: 0, grossAmount: 0, netAmount: 0 },
      AVIATION_SERVICES: { numTrx: 0, grossAmount: 0, netAmount: 0 },
      HOSPITALITY: { numTrx: 0, grossAmount: 0, netAmount: 0 },
      AIRPORTS: { numTrx: 0, grossAmount: 0, netAmount: 0 },
      TOURISM_DEVELOPMENT: { numTrx: 0, grossAmount: 0, netAmount: 0 },
    },
  });

  const [chartData, setChartData] = useState(null);
  const [topSpenders, setTopSpenders] = useState([]);
  const [topSpendersLoading, setTopSpendersLoading] = useState(false);
  const [mostPurchasedItems, setMostPurchasedItems] = useState([]);
  const [mostPurchasedItemsLoading, setMostPurchasedItemsLoading] = useState(false);
  const [memberSummary, setMemberSummary] = useState(null);
  const [memberSummaryLoading, setMemberSummaryLoading] = useState(false);

  const { isOpen: isDetailModalOpen, onOpen: onDetailModalOpen, onClose: onDetailModalClose } = useDisclosure();
  const [detailTransactions, setDetailTransactions] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailTitle, setDetailTitle] = useState('');

  const memberLabels = getMemberLabels(selectedMember);
  const COLORS = getBrandColors(selectedMember);

  useEffect(() => {
    if (!isInj && userMember) {
      setSelectedMember(userMember);
      setShowDetail(true);
    }
  }, [isInj, userMember]);

  useEffect(() => {
    const fetchSummaryData = async () => {
      if (!dateFrom || !dateTo) return;
      setLoading(true);
      try {
        const range = toApiDateRange(dateFrom, dateTo);
        const retailResponse = await productRetailTransactionApi.get('/summary/stats', {
          params: { ...range, onlyInjourneyBesties: true },
        });
        const aviationResponse = await productServicesApi.get('/summary/stats', {
          params: range,
        });

        const retailSummary = retailResponse.data?.status === 200 ? retailResponse.data.data : null;
        const aviationSummary = aviationResponse.data?.status === 200 ? aviationResponse.data.data : null;

        const retailGross = retailSummary?.total_gross_amount || 0;
        const retailNet = retailSummary?.total_net_amount || 0;
        const retailOrders = retailSummary?.total_orders || 0;
        const retailCustomers = retailSummary?.total_customers || 0;
        const aviationGross = aviationSummary?.total_gross_amount ?? 0;
        const aviationNet = aviationSummary?.total_net_amount ?? 0;
        const aviationOrders = aviationSummary?.total_orders ?? 0;
        const aviationCashiers = aviationSummary?.total_cashiers ?? 0;
        const totalGrossAmount = retailGross + aviationGross;
        const totalNetAmount = retailNet + aviationNet;
        const totalOrders = retailOrders + aviationOrders;
        const totalUsers = retailCustomers + aviationCashiers;
        const totalActiveEmployee = 0; // TODO: fetch from employee API
        const usageRatio = totalActiveEmployee > 0 ? (totalUsers / totalActiveEmployee) * 100 : 0;
        const totalDiscAmount = totalGrossAmount - totalNetAmount;
        const avgDiscRate = totalGrossAmount > 0 ? (totalDiscAmount / totalGrossAmount) * 100 : 0;

        setDashboardData({
          allInjourney: {
            totalTrx: totalOrders,
            totalGrossAmount,
            totalUser: totalUsers,
            totalDiscAmount,
            totalActiveEmployee,
            totalNetAmount,
            usageRatio,
            avgDiscRate,
            avgTicketSize: 0,
          },
          byMember: {
            DESTINATION: { numTrx: 0, grossAmount: 0, netAmount: 0 },
            RETAIL: { numTrx: retailOrders, grossAmount: retailGross, netAmount: retailNet },
            AVIATION_SERVICES: { numTrx: aviationOrders, grossAmount: aviationGross, netAmount: aviationNet },
            HOSPITALITY: { numTrx: 0, grossAmount: 0, netAmount: 0 },
            AIRPORTS: { numTrx: 0, grossAmount: 0, netAmount: 0 },
            TOURISM_DEVELOPMENT: { numTrx: 0, grossAmount: 0, netAmount: 0 },
          },
        });
      } catch (_error) {
        toast({ title: 'Error', description: 'Gagal mengambil data summary', status: 'error', duration: 3000, isClosable: true });
      } finally {
        setLoading(false);
      }
    };

    fetchSummaryData();
  }, [dateFrom, dateTo, toast]);

  useEffect(() => {
    const run = async () => {
      if (!showDetail || (selectedMember !== 'RETAIL' && selectedMember !== 'AVIATION_SERVICES')) {
        setChartData(null);
        setMemberSummary(null);
        setTopSpenders([]);
        setMostPurchasedItems([]);
        return;
      }
      const range = toApiDateRange(dateFrom, dateTo);
      const api = selectedMember === 'RETAIL' ? productRetailTransactionApi : productServicesApi;
      const params = selectedMember === 'RETAIL' ? { ...range, onlyInjourneyBesties: true } : range;

      setChartLoading(true);
      setMemberSummaryLoading(true);
      if (selectedMember === 'RETAIL') {
        setTopSpendersLoading(true);
        setMostPurchasedItemsLoading(true);
      }
      try {
        const [chartRes, summaryRes, topRes, itemsRes] = await Promise.all([
          api.get('/chart/data', { params }),
          api.get('/summary/stats', { params }),
          selectedMember === 'RETAIL' ? api.get('/top-spenders', { params }) : Promise.resolve(null),
          selectedMember === 'RETAIL' ? api.get('/most-purchased-items', { params }) : Promise.resolve(null),
        ]);
        if (chartRes?.data?.status === 200) setChartData(chartRes.data.data);
        if (summaryRes?.data?.status === 200) setMemberSummary(summaryRes.data.data);
        if (topRes?.data?.status === 200) setTopSpenders(topRes.data.data || []);
        if (itemsRes?.data?.status === 200) setMostPurchasedItems(itemsRes.data.data || []);
      } catch (_error) {
        toast({ title: 'Error', description: 'Gagal mengambil data chart', status: 'error', duration: 3000, isClosable: true });
      } finally {
        setChartLoading(false);
        setMemberSummaryLoading(false);
        setTopSpendersLoading(false);
        setMostPurchasedItemsLoading(false);
      }
    };

    run();
  }, [dateFrom, dateTo, selectedMember, showDetail, toast]);

  const fetchDetailList = async (params, title) => {
    setDetailTitle(title);
    setDetailLoading(true);
    onDetailModalOpen();
    try {
      const api = selectedMember === 'RETAIL' ? productRetailTransactionApi : productServicesApi;
      const response = await api.get('/list', { params: { ...params, page: 1, limit: 100 } });
      if (response?.data?.status === 200) setDetailTransactions(response.data.data || []);
    } catch (_error) {
      toast({ title: 'Error', description: 'Gagal mengambil detail transaksi', status: 'error', duration: 3000, isClosable: true });
    } finally {
      setDetailLoading(false);
    }
  };

  const handleShowCustomerDetail = async (customerName) => {
    const range = toApiDateRange(dateFrom, dateTo);
    const params =
      selectedMember === 'RETAIL'
        ? { ...range, onlyInjourneyBesties: true, customerName }
        : { ...range, cashierName: customerName };
    await fetchDetailList(params, `Transaction Details: ${customerName}`);
  };

  const handleShowProductDetail = async (productName) => {
    const range = toApiDateRange(dateFrom, dateTo);
    const params = selectedMember === 'RETAIL' ? { ...range, onlyInjourneyBesties: true, productName } : { ...range, productName };
    await fetchDetailList(params, `Transaction Details: ${productName}`);
  };

  const handleDetailClick = (member) => {
    setSelectedMember(member);
    setShowDetail(true);
  };

  return {
    isInj,
    userMember,
    loading,
    dateFrom,
    setDateFrom,
    dateTo,
    setDateTo,
    dashboardData,
    showDetail,
    selectedMember,
    chartLoading,
    chartData,
    topSpendersLoading,
    topSpenders,
    mostPurchasedItemsLoading,
    mostPurchasedItems,
    memberSummaryLoading,
    memberSummary,
    memberLabels,
    COLORS,
    handleDetailClick,
    handleShowCustomerDetail,
    handleShowProductDetail,
    isDetailModalOpen,
    onDetailModalClose,
    detailTitle,
    detailLoading,
    detailTransactions,
  };
};

